import React, { useEffect, useMemo, useState } from 'react';

import { tickerAPI, matchMetaAPI } from '../../services/api';

import {

    toStr,

    lineupPlayerKey,

    rosterEntryKey,

    getOnFieldPlayers,

    getBenchPlayers,

    getSubbedOutIds,

    getSubbedInIds,

    playerDisplayNameFromEntry,

    applySubstitutionToLineup,

    buildTickerPlayerPayload,

    buildTickerSubPayload,

    getLineupPlayerProfile,

} from '../../utils/tickerLineupUtils';



const FLOW_META = {

    goal: { label: 'Tor', icon: '⚽', cls: 'ticker-inline--goal' },

    yellow: { label: 'Gelbe Karte', icon: '🟨', cls: 'ticker-inline--yellow' },

    red: { label: 'Rote Karte', icon: '🟥', cls: 'ticker-inline--red' },

    sub: { label: 'Wechsel', icon: '🔄', cls: 'ticker-inline--sub' },

};



function PlayerGrid({ players, selectedId, onSelect, getNum }) {

    if (!players.length) {

        return <p className="ticker-inline-empty">Keine Spieler verfügbar</p>;

    }



    return (

        <div className="ticker-player-grid">

            {players.map((entry) => {

                const key = lineupPlayerKey(entry);

                const profile = getLineupPlayerProfile(entry);

                const num = getNum(key) || entry.num || profile.num;

                return (

                    <button

                        key={key}

                        type="button"

                        className={`ticker-player-chip ${selectedId === key ? 'ticker-player-chip--active' : ''}`}

                        onClick={() => onSelect(key)}

                    >

                        <span className="ticker-player-chip-num">{num || '–'}</span>

                        <span className="ticker-player-chip-name">{profile.surname || '?'}</span>

                    </button>

                );

            })}

        </div>

    );

}



export default function TickerInlineAction({

    flow,

    gameId,

    game,

    lineup,

    events,

    availablePlayers,

    meta,

    displayMinute,

    onClose,

    onCreated,

    onMetaUpdate,

    onLineupChange,

    onUndoRegister,

    preset = null,

}) {

    const [teamId, setTeamId] = useState(preset?.teamId || null);

    const [playerKey, setPlayerKey] = useState(null);

    const [playerOutKey, setPlayerOutKey] = useState(null);

    const [playerInKey, setPlayerInKey] = useState(null);

    const [note, setNote] = useState('');

    const [submitting, setSubmitting] = useState(false);



    useEffect(() => {

        if (!preset) return;

        if (preset.teamId) setTeamId(preset.teamId);

        const key = preset.playerKey || (preset.plid ? `pl:${preset.plid}` : null);

        if (flow === 'sub' && key) {

            if (preset.step === 'in') setPlayerInKey(key);

            else setPlayerOutKey(key);

        } else if (key) {

            setPlayerKey(key);

        }

    }, [preset, flow]);



    const homeId = toStr(game?.hid?._id || game?.hid);

    const awayId = toStr(game?.aid?._id || game?.aid);

    const homeName = game?.hid?.name || 'Heim';

    const awayName = game?.aid?.name || 'Gast';

    const flowMeta = FLOW_META[flow];

    const currentFT = meta?.scoreFT || { home: game?.scoreh ?? 0, away: game?.scorea ?? 0 };

    const minute = displayMinute || 0;



    const getNum = (key) => {

        const pt = availablePlayers?.find((p) => rosterEntryKey(p) === toStr(key));

        return pt?.num || '';

    };



    const findPlayerEntry = (key) => {

        return lineup.find((entry) => lineupPlayerKey(entry) === toStr(key));

    };



    const cleanName = (key) => {

        const entry = findPlayerEntry(key);

        if (entry) return playerDisplayNameFromEntry(entry);

        const pt = availablePlayers?.find((p) => rosterEntryKey(p) === toStr(key));

        if (pt?.plid) return playerDisplayNameFromEntry({ plid: pt.plid });

        return '?';

    };



    const teamPlayers = useMemo(() => {

        if (!teamId) return [];

        if (flow === 'sub') {

            if (!playerOutKey) {

                return getOnFieldPlayers(lineup, teamId, getSubbedOutIds(events, teamId));

            }

            return getBenchPlayers(lineup, teamId, getSubbedInIds(events, teamId));

        }

        return getOnFieldPlayers(lineup, teamId, getSubbedOutIds(events, teamId))

            .concat(getBenchPlayers(lineup, teamId, getSubbedInIds(events, teamId)));

    }, [teamId, flow, lineup, events, playerOutKey]);



    const updateScore = async (scoreData) => {

        const updated = await matchMetaAPI.update(gameId, { scoreFT: scoreData });

        onMetaUpdate(updated.data);

    };



    const submit = async () => {

        if (submitting) return;

        setSubmitting(true);

        try {

            const teamName = teamId === homeId ? homeName : awayName;

            let type;

            let text;

            let extra = { tid: teamId, published: true, half: meta?.currentHalf || 1 };



            if (flow === 'goal') {

                type = 2;

                const newScore = { ...currentFT };

                if (teamId === homeId) newScore.home = (newScore.home || 0) + 1;

                else newScore.away = (newScore.away || 0) + 1;

                text = `TOR! ${teamName}${playerKey ? ` – ${cleanName(playerKey)}` : ''}. Spielstand: ${newScore.home}:${newScore.away}${note ? `. ${note}` : ''}`;

                extra = { ...extra, ...buildTickerPlayerPayload(playerKey) };

                await updateScore(newScore);

            } else if (flow === 'yellow') {

                type = 3;

                text = `Gelbe Karte: ${playerKey ? cleanName(playerKey) : teamName}`;

                extra = { ...extra, ...buildTickerPlayerPayload(playerKey) };

            } else if (flow === 'red') {

                type = 4;

                text = `Rote Karte: ${playerKey ? cleanName(playerKey) : teamName}`;

                extra = { ...extra, ...buildTickerPlayerPayload(playerKey) };

            } else if (flow === 'sub') {

                if (!playerOutKey || !playerInKey || playerOutKey === playerInKey) return;

                type = 5;

                text = `Wechsel ${teamName}: ${cleanName(playerOutKey)} ⬇️ / ${cleanName(playerInKey)} ⬆️`;

                extra = { ...extra, ...buildTickerSubPayload(playerOutKey, playerInKey) };

            } else {

                return;

            }



            const res = await tickerAPI.create({

                gid: gameId,

                type,

                minute,

                textRaw: text,

                textPublished: text,

                ...extra,

            });



            if (flow === 'sub') {

                onLineupChange?.(applySubstitutionToLineup(lineup, playerOutKey, playerInKey));

            }



            onCreated(res.data);

            onUndoRegister?.(res.data, flowMeta.label);

            onClose();

        } catch (err) {

            console.error(err);

        } finally {

            setSubmitting(false);

        }

    };



    const canSubmit = () => {

        if (!teamId) return false;

        if (flow === 'sub') return !!(playerOutKey && playerInKey && playerOutKey !== playerInKey);

        return true;

    };



    const subStep = flow === 'sub' ? (playerOutKey ? 'in' : 'out') : null;



    return (

        <div className={`ticker-inline ${flowMeta.cls}`}>

            <div className="ticker-inline-head">

                <span>{flowMeta.icon} {flowMeta.label}</span>

                <span className="ticker-inline-minute">{minute}&apos;</span>

                <button type="button" className="ticker-inline-cancel" onClick={onClose}>Abbrechen</button>

            </div>



            {!teamId ? (

                <div className="ticker-inline-team-pick">

                    <button type="button" className="ticker-team-btn" onClick={() => setTeamId(homeId)}>

                        🏠 {homeName}

                    </button>

                    <button type="button" className="ticker-team-btn" onClick={() => setTeamId(awayId)}>

                        ✈️ {awayName}

                    </button>

                </div>

            ) : (

                <>

                    {flow === 'sub' ? (

                        <>

                            <p className="ticker-inline-step">

                                {subStep === 'out' ? 'Spieler raus (auf dem Feld)' : 'Spieler rein (Bank)'}

                            </p>

                            <PlayerGrid

                                players={teamPlayers}

                                selectedId={subStep === 'out' ? playerOutKey : playerInKey}

                                onSelect={(id) => {

                                    if (subStep === 'out') setPlayerOutKey(id);

                                    else setPlayerInKey(id);

                                }}

                                getNum={getNum}

                            />

                        </>

                    ) : (

                        <>

                            <p className="ticker-inline-step">Spieler wählen (optional)</p>

                            <PlayerGrid

                                players={teamPlayers}

                                selectedId={playerKey}

                                onSelect={setPlayerKey}

                                getNum={getNum}

                            />

                            {flow === 'goal' && (

                                <input

                                    type="text"

                                    className="ticker-inline-note"

                                    placeholder="Optional: Torbeschreibung"

                                    value={note}

                                    onChange={(e) => setNote(e.target.value)}

                                />

                            )}

                        </>

                    )}



                    <div className="ticker-inline-actions">

                        <button type="button" className="ticker-clock-btn" onClick={() => {

                            setTeamId(null);

                            setPlayerKey(null);

                            setPlayerOutKey(null);

                            setPlayerInKey(null);

                        }}>

                            ← Team

                        </button>

                        <button

                            type="button"

                            className="ticker-clock-btn ticker-clock-btn--go"

                            disabled={!canSubmit() || submitting}

                            onClick={submit}

                        >

                            {submitting ? '…' : 'Speichern ↵'}

                        </button>

                    </div>

                </>

            )}

        </div>

    );

}

